import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "@/components/ui/sheet";
import { Sparkles, Send, Loader2, Plus, Check, User } from "lucide-react";

// Återanvändbar AI-hjälpare som drawer. Visar förslag med godkännande-knapp när `onApply` ges.
export default function AiAssistant({
  triggerLabel = "AI-hjälp",
  triggerClassName = "",
  triggerVariant = "outline",
  triggerTestId = "ai-assistant-trigger",
  title = "AI-hjälp",
  intro = "Fråga mig om hjälp – jag utgår alltid från dina verkliga uppgifter.",
  suggestions = [],
  loadHistory,
  send,
  onApply,
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [booted, setBooted] = useState(false);
  const [applied, setApplied] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && !booted && loadHistory) {
      setBooted(true);
      loadHistory()
        .then((hist) => setMessages((hist || []).map((m) => ({ role: m.role, content: m.content }))))
        .catch(() => {});
    }
  }, [open, booted, loadHistory]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const submit = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await send(msg);
      setMessages((m) => [...m, { role: "assistant", content: res.reply || "", proposals: res.proposals || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Något gick fel – försök gärna igen om en liten stund." }]);
    } finally {
      setLoading(false);
    }
  };

  const applyProposal = async (mi, pi, p) => {
    if (!onApply) return;
    try {
      await onApply(p);
      setApplied((a) => ({ ...a, [`${mi}-${pi}`]: true }));
    } catch (e) { /* fel hanteras via toast i onApply */ }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant={triggerVariant} className={`rounded-full ${triggerClassName}`} data-testid={triggerTestId}>
          <Sparkles className="h-4 w-4 mr-1.5" /> {triggerLabel}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0" data-testid="ai-assistant-panel">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
          <SheetTitle className="flex items-center gap-2 font-head"><Sparkles className="h-5 w-5 text-primary" /> {title}</SheetTitle>
          <SheetDescription>{intro}</SheetDescription>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="ai-assistant-messages">
          {messages.length === 0 && !loading && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Kom igång – till exempel:</p>
              <div className="flex flex-col gap-2">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => submit(s)} data-testid={`ai-suggestion-${i}`}
                    className="text-left text-sm rounded-xl border border-border px-3 py-2 hover:border-primary/40 hover:bg-primary/[0.03] transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, mi) => (
            <div key={mi} className={m.role === "user" ? "flex justify-end" : ""}>
              {m.role === "user" ? (
                <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-3.5 py-2 text-sm" data-testid={`ai-msg-user-${mi}`}>
                  {m.content}
                </div>
              ) : (
                <div className="max-w-[92%]" data-testid={`ai-msg-assistant-${mi}`}>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Sparkles className="h-3.5 w-3.5 text-primary" /> Coach</div>
                  <div className="rounded-2xl rounded-bl-sm bg-muted px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>
                  {onApply && (m.proposals || []).length > 0 && (
                    <div className="mt-2 space-y-2">
                      {m.proposals.map((p, pi) => (
                        <div key={pi} className="rounded-xl border border-primary/25 bg-primary/[0.04] p-3" data-testid={`ai-proposal-${mi}-${pi}`}>
                          <div className="text-xs font-medium text-primary mb-0.5">{PROPOSAL_LABEL[p.type] || "Förslag"}</div>
                          <div className="text-sm text-foreground mb-2">{proposalPreview(p)}</div>
                          <Button size="sm" className="rounded-full h-8" disabled={applied[`${mi}-${pi}`]}
                            onClick={() => applyProposal(mi, pi, p)} data-testid={`ai-apply-${mi}-${pi}`}>
                            {applied[`${mi}-${pi}`] ? <><Check className="h-3.5 w-3.5 mr-1" /> Tillagt</> : <><Plus className="h-3.5 w-3.5 mr-1" /> Lägg till</>}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ai-assistant-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Coachen tänker …
            </div>
          )}
        </div>

        <div className="border-t border-border p-3 flex items-end gap-2">
          <Textarea
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder="Skriv ditt meddelande …"
            className="resize-none min-h-[44px]"
            data-testid="ai-assistant-input"
          />
          <Button size="icon" className="rounded-full shrink-0 h-11 w-11" onClick={() => submit()} disabled={loading || !input.trim()} data-testid="ai-assistant-send">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const PROPOSAL_LABEL = {
  profile: "Ny professionell profil",
  competency: "Kärnkompetens",
  strength: "Personlig styrka",
  system: "System / verktyg",
  language: "Språk",
  certification: "Certifiering",
  experience_bullet: "Punkt till erfarenhet",
  experience: "Ny erfarenhet",
  skill: "Kompetens",
  education: "Utbildning",
  direction_field: "Framtidsriktning",
};

function proposalPreview(p) {
  if (p.type === "experience" && p.value && typeof p.value === "object") {
    const v = p.value;
    return `${v.role || ""}${v.employer ? `, ${v.employer}` : ""}${v.period ? ` (${v.period})` : ""}`;
  }
  if (p.type === "skill" && p.value && typeof p.value === "object") {
    return `${p.value.name || ""}${p.value.level ? ` – ${p.value.level}` : ""}`;
  }
  if (p.type === "education" && p.value && typeof p.value === "object") {
    const v = p.value;
    return `${v.name || ""}${v.school ? `, ${v.school}` : ""}`;
  }
  if (p.type === "experience_bullet") return `”${p.value}”`;
  return String(p.value || p.text || "");
}
